import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5126';

const DAY_LABELS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const DAY_SHORT  = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];

const formatDateFR = (isoStr) => {
  const [y, m, d] = isoStr.split('-');
  return new Date(y, m - 1, d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminDisponibilites = () => {
  const [schedule, setSchedule] = useState([]);
  const [slots, setSlots] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newSlot, setNewSlot] = useState('');
  const [slotError, setSlotError] = useState('');

  const [overrideDate, setOverrideDate] = useState('');
  const [overrideIsOpen, setOverrideIsOpen] = useState(false);
  const [overrideError, setOverrideError] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, sl, ov] = await Promise.all([
        axios.get(`${API}/availability/schedule`),
        axios.get(`${API}/availability/timeslots`),
        axios.get(`${API}/availability/overrides`),
      ]);
      setSchedule(s.data);
      setSlots(sl.data);
      setOverrides(ov.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleDay = async (day, current) => {
    setSchedule(prev => prev.map(d => d.day === day ? { ...d, isOpen: !current } : d));
    await axios.patch(`${API}/availability/schedule/${day}`, { isOpen: !current });
  };

  const addSlot = async (e) => {
    e.preventDefault();
    setSlotError('');
    const time = newSlot.trim();
    if (!time.match(/^\d{2}:\d{2}$/)) { setSlotError('Format invalide (ex: 09:00)'); return; }
    if (slots.some(s => s.time === time)) { setSlotError('Ce créneau existe déjà.'); return; }
    try {
      const { data } = await axios.post(`${API}/availability/timeslots`, { time });
      setSlots(prev => [...prev, data].sort((a, b) => a.time.localeCompare(b.time)));
      setNewSlot('');
    } catch {
      setSlotError('Erreur lors de l\'ajout.');
    }
  };

  const deleteSlot = async (id) => {
    await axios.delete(`${API}/availability/timeslots/${id}`);
    setSlots(prev => prev.filter(s => s.id !== id));
  };

  const addOverride = async (e) => {
    e.preventDefault();
    setOverrideError('');
    if (!overrideDate) { setOverrideError('Sélectionnez une date.'); return; }
    try {
      const { data } = await axios.post(`${API}/availability/overrides`, { date: overrideDate, isOpen: overrideIsOpen });
      setOverrides(prev => {
        const filtered = prev.filter(o => o.date !== overrideDate);
        return [...filtered, data].sort((a, b) => a.date.localeCompare(b.date));
      });
      setOverrideDate('');
    } catch {
      setOverrideError('Erreur lors de l\'ajout.');
    }
  };

  const deleteOverride = async (id) => {
    await axios.delete(`${API}/availability/overrides/${id}`);
    setOverrides(prev => prev.filter(o => o.id !== id));
  };

  if (loading) return <div className="admin-spinner">Chargement...</div>;

  const orderedSchedule = [1, 2, 3, 4, 5, 6, 0].map(d => schedule.find(s => s.day === d)).filter(Boolean);

  return (
    <>
      <div className="admin-page-title">Disponibilités</div>
      <div className="admin-page-sub">Gérez vos jours, créneaux horaires et exceptions.</div>

      {/* Jours de la semaine */}
      <div className="admin-card" style={{ marginBottom: 24 }}>
        <div className="admin-section-title">Jours de la semaine</div>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 16 }}>
          Activez les jours où vous êtes disponible pour un appel.
        </p>
        <div className="dispo-days-grid">
          {orderedSchedule.map(({ day, isOpen }) => (
            <button
              key={day}
              className={`dispo-day-btn${isOpen ? ' open' : ''}`}
              onClick={() => toggleDay(day, isOpen)}
            >
              <span className="dispo-day-short">{DAY_SHORT[day]}</span>
              <span className="dispo-day-full">{DAY_LABELS[day]}</span>
              <span className="dispo-day-status">{isOpen ? 'Ouvert' : 'Fermé'}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Créneaux horaires */}
      <div className="admin-card" style={{ marginBottom: 24 }}>
        <div className="admin-section-title">Créneaux horaires</div>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 16 }}>
          Définissez les horaires proposés. Un créneau réservé est automatiquement retiré.
        </p>
        <div className="dispo-slots-list">
          {slots.map(s => (
            <div key={s.id} className="dispo-slot-item">
              <span className="dispo-slot-time">{s.time}</span>
              <button className="admin-btn-icon danger" onClick={() => deleteSlot(s.id)} title="Supprimer">🗑</button>
            </div>
          ))}
          {slots.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>Aucun créneau défini.</div>
          )}
        </div>

        <form onSubmit={addSlot} className="dispo-add-form" style={{ marginTop: 16 }}>
          <input
            className="form-input"
            type="time"
            value={newSlot}
            onChange={e => setNewSlot(e.target.value)}
            step="3600"
            placeholder="09:00"
            style={{ width: 140 }}
          />
          <button type="submit" className="admin-btn admin-btn-primary">Ajouter</button>
          {slotError && <span className="dispo-error">{slotError}</span>}
        </form>
      </div>

      {/* Dates exceptionnelles */}
      <div className="admin-card">
        <div className="admin-section-title">Dates exceptionnelles</div>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 16 }}>
          Forcez l'ouverture ou la fermeture d'une date spécifique, indépendamment du planning hebdomadaire.
        </p>

        {overrides.length > 0 && (
          <div className="dispo-overrides-list" style={{ marginBottom: 16 }}>
            {overrides.map(o => (
              <div key={o.id} className="dispo-override-item">
                <span className={`admin-badge ${o.isOpen ? 'badge-open' : 'badge-closed'}`}>
                  {o.isOpen ? 'Ouvert' : 'Fermé'}
                </span>
                <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{formatDateFR(o.date)}</span>
                <button className="admin-btn-icon danger" onClick={() => deleteOverride(o.id)} title="Supprimer">🗑</button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={addOverride} className="dispo-add-form dispo-override-form">
          <input
            className="form-input"
            type="date"
            value={overrideDate}
            onChange={e => setOverrideDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            style={{ width: 180 }}
          />
          <label className="dispo-toggle-label">
            <input
              type="checkbox"
              checked={overrideIsOpen}
              onChange={e => setOverrideIsOpen(e.target.checked)}
            />
            <span>{overrideIsOpen ? 'Ouvrir cette date' : 'Fermer cette date'}</span>
          </label>
          <button type="submit" className="admin-btn admin-btn-primary">Appliquer</button>
          {overrideError && <span className="dispo-error">{overrideError}</span>}
        </form>
      </div>
    </>
  );
};

export default AdminDisponibilites;
